import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="bg-white">
      {/* Hero section */}
      <div className="relative bg-gradient-to-r from-blue-50 to-gray-50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
              Privacy Policy
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-500">
              Last Updated: April 1, 2025
            </p>
          </div>
        </div>
      </div>

      {/* Content section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-blue mx-auto">
            <h2>Introduction</h2>
            <p>
              Explanatum ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
            </p>
            <p>
              This policy is compliant with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. We are committed to ensuring that your privacy is protected and that we comply with our legal obligations when processing your personal data.
            </p>

            <h2>Information We Collect</h2>
            <p>
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul>
              <li>Register for an account</li>
              <li>Use our business idea generation services</li>
              <li>Subscribe to our newsletter</li>
              <li>Contact us with inquiries</li>
              <li>Participate in promotions or surveys</li>
              <li>Make purchases through our platform</li>
            </ul>
            <p>
              The personal information we collect may include:
            </p>
            <ul>
              <li>Name, email address, and contact details</li>
              <li>Billing information and payment details</li>
              <li>User preferences and settings</li>
              <li>Business information you provide to generate ideas and plans</li>
              <li>Communication history with us</li>
            </ul>

            <h2>Automatically Collected Information</h2>
            <p>
              When you visit our website, we may automatically collect certain information about your device, including:
            </p>
            <ul>
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Time zone setting and location</li>
              <li>Pages you view</li>
              <li>How you interact with our website</li>
            </ul>
            <p>
              We collect this information using cookies and similar technologies. Please see our Cookie Policy for more details.
            </p>

            <h2>Legal Basis for Processing</h2>
            <p>
              Under the UK GDPR, we process your personal data on the following legal bases:
            </p>
            <ul>
              <li><strong>Contract:</strong> Processing necessary for the performance of our contract with you</li>
              <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate interests, provided those interests don't override your fundamental rights and freedoms</li>
              <li><strong>Consent:</strong> Processing based on your specific consent</li>
              <li><strong>Legal Obligation:</strong> Processing necessary to comply with our legal obligations</li>
            </ul>

            <h2>How We Use Your Information</h2>
            <p>
              We use the information we collect to:
            </p>
            <ul>
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Develop new products and services</li>
              <li>Monitor usage patterns and analyze trends</li>
              <li>Protect against, identify, and prevent fraud and other illegal activity</li>
              <li>Comply with our legal obligations</li>
            </ul>

            <h2>Data Retention</h2>
            <p>
              We will retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements.
            </p>
            <p>
              To determine the appropriate retention period, we consider the amount, nature, and sensitivity of the personal data, the potential risk of harm from unauthorized use or disclosure, the purposes for which we process the data, and whether we can achieve those purposes through other means.
            </p>

            <h2>Data Sharing and Disclosure</h2>
            <p>
              We may share your information with:
            </p>
            <ul>
              <li><strong>Service Providers:</strong> Third-party vendors who provide services on our behalf, such as payment processing, data analysis, email delivery, and customer service</li>
              <li><strong>Business Partners:</strong> With your consent, we may share your information with our business partners to offer you certain products or services</li>
              <li><strong>Legal Requirements:</strong> If required to do so by law or in response to valid requests by public authorities</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale of Explanatum Limited's assets, financing, or acquisition of all or a portion of our business</li>
              <li><strong>With Your Consent:</strong> In other ways described to you at the point of data collection and with your consent</li>
            </ul>

            <h2>International Data Transfers</h2>
            <p>
              Your personal data may be transferred to, and processed in, countries other than the country in which you reside. These countries may have data protection laws that are different from the laws of your country.
            </p>
            <p>
              Whenever we transfer your personal data out of the UK, we ensure a similar degree of protection is afforded to it by implementing appropriate safeguards, including:
            </p>
            <ul>
              <li>Using specific contracts approved by the UK Information Commissioner that give personal data the same protection it has in the UK</li>
              <li>Transferring data to countries that have been deemed to provide an adequate level of protection for personal data by the UK government</li>
            </ul>

            <h2>Your Data Protection Rights</h2>
            <p>
              Under the UK GDPR, you have the following rights:
            </p>
            <ul>
              <li><strong>Access:</strong> You can request copies of your personal data</li>
              <li><strong>Rectification:</strong> You can request that we correct inaccurate or complete incomplete data</li>
              <li><strong>Erasure:</strong> You can request that we erase your personal data in certain circumstances</li>
              <li><strong>Restrict Processing:</strong> You can request that we restrict the processing of your data in certain circumstances</li>
              <li><strong>Data Portability:</strong> You can request the transfer of your data to you or a third party</li>
              <li><strong>Object:</strong> You can object to our processing of your personal data in certain circumstances</li>
              <li><strong>Automated Decision-making:</strong> You can request not to be subject to automated decision-making, including profiling</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the details provided below. We may need to request specific information from you to help us confirm your identity and ensure your right to access your personal data. This is a security measure to ensure that personal data is not disclosed to any person who has no right to receive it.
            </p>

            <h2>Data Security</h2>
            <p>
              We have implemented appropriate technical and organizational measures to protect your personal data against unauthorized or unlawful processing, accidental loss, destruction, or damage. However, no method of transmission over the Internet or electronic storage is 100% secure, so we cannot guarantee absolute security.
            </p>

            <h2>Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under the age of 16, and we do not knowingly collect personal data from children. If we learn that we have collected personal data from a child under 16, we will take steps to delete that information as quickly as possible.
            </p>

            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last Updated" date at the top of this Privacy Policy. We encourage you to review this Privacy Policy frequently to be informed of how we are protecting your information.
            </p>

            <h2>Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p>
              Explanatum Limited<br />
              Email: privacy@explanatum.co.uk<br />
              Address: 3 Heathmoor Road, Wirral, CH46 7UN<br />
            </p>

            <h2>Data Protection Officer</h2>
            <p>
              You can contact our Data Protection Officer directly at dpo@explanatum.co.uk.
            </p>

            <h2>Complaints</h2>
            <p>
              If you have a concern about our handling of your personal data, please contact us first. If you are not satisfied with our response, you have the right to lodge a complaint with the Information Commissioner's Office (ICO), the UK supervisory authority for data protection issues (www.ico.org.uk).
            </p>
          </div>
        </div>
      </div>

      {/* CTA section */}
      <div className="bg-blue-600">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Questions about our privacy practices?</span>
            <span className="block text-blue-200">Contact our team for more information.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 transition-all duration-200 ease-in-out"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
